/**
 * The vocabulary of "Empresas que contratan", shared by its three panels.
 *
 * Types, labels and the small pure functions that turn stored values into what
 * a person reads: how long ago something happened, whether a follow-up is due,
 * what to call a contact who has an address and no name. They live here rather
 * than in the view because the contacts panel and the activity panel need the
 * same answers, and two copies of "is this overdue" is one bug waiting for a
 * timezone.
 */

// ── Where an employer lives ──────────────────────────────────────────

/** The query parameters "Empresas que contratan" opens on an employer or a message from. */
export const EMPLOYER_PARAM = "empresa";
export const INBOUND_PARAM = "entrada";

/**
 * The address of one employer in "Empresas que contratan", or of one message
 * that came in through the site.
 *
 * A company's cover links here — "Trabajarla en «Empresas que contratan»" —
 * and without a target the link opened the screen on the top of "Por hacer",
 * leaving the reader to find the company again by name: the same failure
 * Correo had with its conversations. `employer` is the `employer_key`;
 * `inbound` is the id of the `company_leads` or `contact_messages` row.
 */
export function prospectsUrl(target: { employer?: string | null; inbound?: string | null } = {}): string {
    const params = new URLSearchParams();
    if (target.inbound) params.set(INBOUND_PARAM, target.inbound);
    else if (target.employer) params.set(EMPLOYER_PARAM, target.employer);
    const query = params.toString();
    return `/empresas-que-contratan${query ? `?${query}` : ""}`;
}

// ── Statuses ─────────────────────────────────────────────────────────

export type ProspectStatus = "new" | "contacted" | "replied" | "meeting" | "won" | "discarded";

export const TABS: { id: ProspectStatus; label: string }[] = [
    { id: "new", label: "Sin contactar" },
    { id: "contacted", label: "Contactadas" },
    { id: "replied", label: "Respondieron" },
    { id: "meeting", label: "Reunión" },
    { id: "won", label: "Clientes" },
    { id: "discarded", label: "Descartadas" }
];

/**
 * What each filter holds, in one line, under the chips.
 *
 * Correo does the same, for the same reason: six of these chips answer "where
 * does this company stand" and three answer "what do I do", and neither
 * question is answerable from two words on a chip.
 */
export const PROSPECT_TAB_HINT: Record<string, string> = {
    todo: "Empresas a las que toca escribir, o volver a escribir porque no contestaron. Ordenadas por lo que hay que hacer.",
    due: "Las que alguien puso para retomar hoy.",
    mine: "Las que dijiste que llevas tú.",
    inbound: "Se registraron en el formulario de la web y todavía no las ha pasado nadie a la lista.",
    new: "Publicaron una vacante de sostenibilidad y aún no les hemos escrito.",
    contacted: "Les escribimos y todavía no han contestado.",
    replied: "Contestaron. Toca proponerles una reunión.",
    meeting: "Hay una reunión hecha o agendada con ellas.",
    won: "Nos han encargado una búsqueda.",
    discarded: "Descartadas: no encajan, o pidieron que no insistiéramos."
};

export const STATUS_COLOR: Record<ProspectStatus, string> = {
    new: "grayLighter",
    contacted: "blueLighter",
    replied: "cyanLighter",
    meeting: "orangeLighter",
    won: "greenLighter",
    discarded: "redLighter"
};

export function statusLabel(status: ProspectStatus | string | null | undefined): string {
    return TABS.find(t => t.id === status)?.label ?? String(status ?? "");
}

// ── Rows, as the data plane returns them ─────────────────────────────

export interface ScrapedRow {
    id: string;
    title?: string | null;
    company_name?: string | null;
    location?: string | null;
    location_city?: string | null;
    location_country?: string | null;
    employer_website?: string | null;
    employer_logo?: string | null;
    source_url?: string | null;
    application_link?: string | null;
    status?: string | null;
    scraped_at?: string | null;
    posted_at?: string | null;
}

/**
 * How an employer got onto the list.
 *
 * Until 0044 the scrape queue was the only door, which meant the one employer
 * this business most wants to talk to — the one who wrote to us first — could
 * not be recorded at all. `inbound` covers a demo request, the contact form and
 * a cold email in the mailbox; they are the same event wearing three table
 * names, and they all outrank a company we found by scraping.
 */
export type ProspectSource = "scraped" | "inbound" | "manual";

export const SOURCE_LABEL: Record<ProspectSource, string> = {
    scraped: "la vimos publicando empleo",
    inbound: "desde la web",
    manual: "añadida a mano"
};

export interface ProspectRow {
    id: string;
    employer_key: string;
    employer_name?: string | null;
    employer_website?: string | null;
    /** The `companies` row that is this same employer, when one exists. See 0044. */
    company_id?: string | null;
    source?: ProspectSource | null;
    source_detail?: string | null;
    status?: ProspectStatus | null;
    /** The admin working this employer, by email (drizzle/0054). */
    owner?: string | null;
    last_contacted_at?: string | null;
    notes?: string | null;
    next_action?: string | null;
    next_action_at?: string | null;
    contacts_synced_at?: string | null;
    contacts_note?: string | null;
}

export type ContactKind = "person" | "mailbox";
export type EmailStatus = "none" | "pattern_guess" | "published" | "manual";
export type RoleBucket = "talent" | "sustainability" | "leadership" | "other";

export interface ContactRow {
    id: string;
    employer_key: string;
    name_key?: string | null;
    kind?: ContactKind | null;
    full_name?: string | null;
    role_title?: string | null;
    role_bucket?: RoleBucket | null;
    email?: string | null;
    email_status?: EmailStatus | null;
    email_alternatives?: string[] | null;
    linkedin_url?: string | null;
    phone?: string | null;
    source?: string | null;
    source_url?: string | null;
    notes?: string | null;
    created_at?: string | null;
}

export type ActivityKind = "note" | "status" | "email" | "call" | "meeting" | "discovery";

export interface ActivityRow {
    id: string;
    employer_key: string;
    contact_id?: string | null;
    kind?: ActivityKind | null;
    body?: string | null;
    actor?: string | null;
    created_at?: string | null;
}

/** One employer, with everything the screen knows about them. */
export interface Employer {
    key: string;
    name: string;
    website: string | null;
    logo: string | null;
    /**
     * Vacancies still live: pending review or published.
     *
     * Empty is now a real state rather than an impossible one. An employer who
     * wrote to us has no scraped offer at all, and the screen has to hold them
     * anyway — see the union in ProspectsView's `employers`.
     */
    open: ScrapedRow[];
    /** Vacancies that timed out. Evidence they hire, not that they are hiring. */
    past: ScrapedRow[];
    countries: string[];
    /** Most recent posting date across every offer — the buying-intent signal. */
    lastSeen: string | null;
    prospect: ProspectRow | null;
    status: ProspectStatus;
    contacts: ContactRow[];
    /** Contacts we could actually write to today. */
    reachable: number;
    /** The brand record for this same employer, when one exists. Null for most. */
    companyId: string | null;
    source: ProspectSource;
}

/** Whether this employer came to us. Sorts ahead of cold work at the same stage. */
export function isInbound(employer: Pick<Employer, "source">): boolean {
    return employer.source === "inbound";
}

// ── Labels ───────────────────────────────────────────────────────────

export const ROLE_LABEL: Record<RoleBucket, string> = {
    talent: "Talento / RRHH",
    sustainability: "Sostenibilidad",
    leadership: "Dirección",
    other: "Otro"
};

export const ROLE_COLOR: Record<RoleBucket, string> = {
    talent: "greenLighter",
    sustainability: "cyanLighter",
    leadership: "purpleLighter",
    other: "grayLighter"
};

/** Talent first: they are who buys a placement. See drizzle/0040. */
export const ROLE_ORDER: Record<RoleBucket, number> = {
    talent: 0,
    sustainability: 1,
    leadership: 2,
    other: 3
};

/**
 * How an address is described, and it is never "verified".
 *
 * The wording is deliberately blunt on the guess: "patrón" reads as a fact
 * about the address rather than a quality score, and somebody about to send a
 * first email needs to know it may bounce before they write it, not after.
 */
export const EMAIL_STATUS_LABEL: Record<EmailStatus, string> = {
    none: "sin email",
    pattern_guess: "dirección supuesta",
    published: "publicado",
    manual: "introducido a mano"
};

export const EMAIL_STATUS_COLOR: Record<EmailStatus, string> = {
    none: "grayLighter",
    pattern_guess: "orangeLighter",
    published: "blueLighter",
    manual: "greenLighter"
};

export const ACTIVITY_LABEL: Record<ActivityKind, string> = {
    note: "Nota",
    status: "Estado",
    email: "Email",
    call: "Llamada",
    meeting: "Reunión",
    discovery: "Búsqueda"
};

export const ACTIVITY_COLOR: Record<ActivityKind, string> = {
    note: "grayLighter",
    status: "blueLighter",
    email: "cyanLighter",
    call: "orangeLighter",
    meeting: "purpleLighter",
    discovery: "greenLighter"
};

// ── Time ─────────────────────────────────────────────────────────────

/** Local midnight of a date, which is the unit every label below counts in. */
function startOfDay(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Whole calendar days from today to `iso` — negative for the past.
 *
 * Calendar days, not elapsed hours, and both labels below depend on it. An
 * offer scraped yesterday at noon is "ayer" whether it is read at 09:00 or at
 * 00:30, and a follow-up set for this morning is still due this afternoon.
 * Counting hours instead makes both of those flip mid-day, which is how a queue
 * stops being trusted.
 */
function calendarDaysTo(iso: string): number | null {
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return null;
    return Math.round((startOfDay(when) - startOfDay(new Date())) / 86_400_000);
}

/** "hace 3 días" — a past date is only useful here as a distance from today. */
export function sinceLabel(iso: string | null | undefined): string {
    if (!iso) return "sin fecha";
    const delta = calendarDaysTo(iso);
    if (delta === null) return "sin fecha";
    const days = -delta;
    if (days <= 0) return "hoy";
    if (days === 1) return "ayer";
    if (days < 30) return `hace ${days} días`;
    const months = Math.floor(days / 30);
    return months === 1 ? "hace 1 mes" : `hace ${months} meses`;
}

export type DueState = "overdue" | "today" | "soon" | "later" | "none";

/**
 * Whether a follow-up is due, by calendar day rather than by elapsed hours.
 *
 * "Mañana a las 9" set on Monday afternoon is due on Tuesday, not 17 hours from
 * when it was typed — so both dates are floored to local midnight before they
 * are compared. Getting this wrong shows tomorrow's work as due today, every
 * afternoon, which is the fastest way to make a queue untrustworthy.
 */
export function dueState(iso: string | null | undefined): DueState {
    if (!iso) return "none";
    const days = calendarDaysTo(iso);
    if (days === null) return "none";
    if (days < 0) return "overdue";
    if (days === 0) return "today";
    if (days <= 7) return "soon";
    return "later";
}

/** "atrasado 3 días", "hoy", "en 5 días" — the queue's own unit. */
export function dueLabel(iso: string | null | undefined): string {
    if (!iso) return "";
    const days = calendarDaysTo(iso);
    if (days === null) return "";
    if (days < -1) return `atrasado ${Math.abs(days)} días`;
    if (days === -1) return "atrasado 1 día";
    if (days === 0) return "hoy";
    if (days === 1) return "mañana";
    return `en ${days} días`;
}

export const DUE_COLOR: Record<DueState, string> = {
    overdue: "redLighter",
    today: "orangeLighter",
    soon: "blueLighter",
    later: "grayLighter",
    none: "grayLighter"
};

/** Today plus N days at 09:00 local — a follow-up is a morning's work. */
export function inDays(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() + days);
    date.setHours(9, 0, 0, 0);
    return date;
}

// ── What to do next ──────────────────────────────────────────────────

/**
 * The one thing an employer needs next, derived from the record.
 *
 * The status is what somebody *declared* about a company. The stage is what the
 * record *says has to happen* to it, and they are not the same question — only
 * the second one can be answered without opening the card. Thirty employers
 * sitting in "Sin contactar" are three different mornings' work: some have
 * nobody to write to yet, some have a person with no address, some have an
 * address and are waiting for a first email. One label hid all of that, which is
 * why the screen read as a list to browse rather than a queue to work.
 *
 * Exactly one stage per employer, and the order of the rules below is the order
 * they are read in: a follow-up that came due outranks everything, and a
 * follow-up scheduled for later silences the rest until its date.
 */
export type Stage = "due" | "write" | "chase" | "reach" | "find" | "waiting" | "closed";

export function stageOf(
    employer: Pick<Employer, "status" | "prospect" | "contacts" | "reachable">
): Stage {
    if (employer.status === "won" || employer.status === "discarded") return "closed";

    const due = dueState(employer.prospect?.next_action_at);
    if (due === "overdue" || due === "today") return "due";
    // A follow-up set for next week is a decision somebody already took. Asking
    // for "buscar personas" on top of it is asking for the same work twice.
    if (due !== "none") return "waiting";

    if (employer.status === "new") {
        if (employer.contacts.length === 0) return "find";
        if (employer.reachable === 0) return "reach";
        return "write";
    }

    // Contacted, answered or met, and nothing scheduled — the state in which
    // conversations are lost without anybody deciding to lose them.
    return "chase";
}

/** The stage as an instruction. Every one of these is a verb on purpose. */
export const STAGE_LABEL: Record<Stage, string> = {
    due: "Toca hoy",
    write: "Escribir el primer email",
    chase: "Programar el seguimiento",
    reach: "Conseguir un email",
    find: "Buscar personas",
    waiting: "En espera",
    closed: "Cerrada"
};

export const STAGE_COLOR: Record<Stage, string> = {
    due: "redLighter",
    write: "greenLighter",
    chase: "orangeLighter",
    reach: "cyanLighter",
    find: "purpleLighter",
    waiting: "grayLighter",
    closed: "grayLighter"
};

/**
 * The order the work is worth doing in, which is not the order of the funnel.
 *
 * Writing to somebody we can already reach comes first because it is the only
 * step that can produce a client today. Chasing a conversation nobody scheduled
 * is next, because that one decays. Research — one field away, then a whole
 * search — comes last: it is the most work per employer and the least urgent.
 */
export const STAGE_ORDER: Record<Stage, number> = {
    due: 0, write: 1, chase: 2, reach: 3, find: 4, waiting: 5, closed: 6
};

/** The stages that are work. "Por hacer" is exactly this set. */
export const TODO_STAGES: Stage[] = ["due", "write", "chase", "reach", "find"];

export function isTodo(stage: Stage): boolean {
    return TODO_STAGES.includes(stage);
}

/** What the button in the "Lo siguiente" card says. Empty where there is nothing to press. */
export const STAGE_ACTION: Record<Stage, string> = {
    due: "Marcar hecho",
    write: "Escribir",
    chase: "Recordar en 3 días",
    reach: "Añadir un email",
    find: "Buscar personas",
    waiting: "",
    closed: ""
};

/**
 * Why this employer is at this stage, in one sentence and with its own facts.
 *
 * "Buscar personas" is an instruction; "nadie ha buscado todavía quién lleva la
 * contratación aquí" is a reason. The screen was never short of controls — it
 * was short of a sentence saying which one to press and why.
 */
export function stageHint(employer: Employer): string {
    switch (stageOf(employer)) {
        case "due":
            return employer.prospect?.next_action?.trim()
                || "Hay un seguimiento vencido sin texto: mira la actividad y decide.";
        case "write": {
            const vacancy = employer.open[0]?.title?.trim();
            if (vacancy) return `Tienen abierta "${vacancy}" y aquí hay a quién escribir.`;
            // Three different situations behind "no open vacancy", and only one
            // of them is a lapsed prospect. An employer who wrote to us never
            // had a scraped offer at all, and telling somebody to mention the
            // vacancy they published would be an instruction to invent one.
            if (isInbound(employer)) {
                return `Escribieron ellos${employer.prospect?.source_detail ? ` (${employer.prospect.source_detail})` : ""}. Responde antes de que se enfríe.`;
            }
            return employer.past.length > 0
                ? "Hay a quién escribir, aunque su vacante ya caducó: menciona la que publicaron."
                : "Hay a quién escribir. No tienen ninguna vacante nuestra detrás, así que da tú el motivo.";
        }
        case "chase":
            return employer.prospect?.last_contacted_at
                ? `Contactada ${sinceLabel(employer.prospect.last_contacted_at)} y sin nada programado.`
                : "No hay nada programado, y así es como se pierden las conversaciones.";
        case "reach":
            return employer.contacts.length === 1
                ? "Hay una persona, pero sin email ni LinkedIn con el que llegar a ella."
                : `Hay ${employer.contacts.length} personas, pero ninguna con email ni LinkedIn.`;
        case "find":
            return employer.prospect?.contacts_synced_at
                ? `La última búsqueda (${sinceLabel(employer.prospect.contacts_synced_at)}) no dejó a nadie. Repítela o añade a alguien a mano.`
                : "Nadie ha buscado todavía quién lleva la contratación aquí.";
        case "waiting":
            return `Siguiente paso ${dueLabel(employer.prospect?.next_action_at)}${
                employer.prospect?.next_action ? `: ${employer.prospect.next_action}` : ""
            }.`;
        case "closed":
            return "";
    }
}

// ── Contacts ─────────────────────────────────────────────────────────

/**
 * What to call a contact on screen.
 *
 * Three cases and none of them may invent a name: a person has one, a mailbox
 * is its address, and a LinkedIn profile found in a posting is its handle. The
 * last one is deliberately not title-cased into "Ana Garcia Lopez" — a slug is
 * not a spelling, and a wrong name in a greeting is worse than a handle.
 */
export function contactLabel(contact: ContactRow): string {
    const name = (contact.full_name ?? "").trim();
    if (name) return name;
    const email = (contact.email ?? "").trim();
    if (email) return email;
    const handle = /linkedin\.com\/in\/([^/?#]+)/i.exec(contact.linkedin_url ?? "")?.[1];
    if (handle) return `linkedin.com/in/${handle}`;
    return "Contacto sin nombre";
}

/**
 * The dedupe identity of a contact, as the database's UNIQUE index expects it.
 *
 * The same derivation as `contactNameKey` in
 * backend/src/services/contact-discovery.ts, and it has to stay that way: a
 * person added by hand here and found by discovery there must land on one key,
 * or the same human appears twice with two half-filled cards. The backend owns
 * the canonical version; this is the copy the browser needs, and the pair is
 * covered by a test that folds the same names through both.
 *
 * Falls back to the address, then to the LinkedIn handle — the same three
 * identities `contactLabel` displays, in the same order.
 */
export function contactNameKey(contact: Pick<ContactRow, "full_name" | "email" | "linkedin_url">): string {
    const fold = (value: string) => value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    const name = (contact.full_name ?? "").trim();
    if (name) return fold(name);
    const email = (contact.email ?? "").trim();
    if (email) return fold(email);
    const handle = /linkedin\.com\/in\/([^/?#]+)/i.exec(contact.linkedin_url ?? "")?.[1];
    return handle ? fold(handle) : "";
}

/** The greeting name: first token only, and empty when there is no name. */
export function firstNameOf(contact: ContactRow): string {
    const name = (contact.full_name ?? "").trim();
    if (!name) return "";
    return name.split(/\s+/)[0] ?? "";
}

export function roleOf(contact: ContactRow): RoleBucket {
    const bucket = contact.role_bucket;
    return bucket && bucket in ROLE_ORDER ? bucket : "other";
}

export function emailStatusOf(contact: ContactRow): EmailStatus {
    const status = contact.email_status;
    return status && status in EMAIL_STATUS_LABEL ? status : "none";
}

/** Named people before mailboxes, in the order their role matters. */
export function compareContacts(a: ContactRow, b: ContactRow): number {
    const byRole = ROLE_ORDER[roleOf(a)] - ROLE_ORDER[roleOf(b)];
    if (byRole !== 0) return byRole;
    const byKind = (a.kind === "mailbox" ? 1 : 0) - (b.kind === "mailbox" ? 1 : 0);
    if (byKind !== 0) return byKind;
    return contactLabel(a).localeCompare(contactLabel(b));
}

// ── The first email ──────────────────────────────────────────────────

/**
 * A draft for the first message to one contact at one employer.
 *
 * This is the draft for *Abrir en mi correo* — the `mailto:` path, kept for the
 * message somebody would rather send under their own name from their own
 * address. Mail sent from the panel itself goes through the composer and
 * `functions/send-email` instead, and its draft comes from the model with the
 * account's real facts behind it (see "Correo" in the README).
 *
 * Deliberately still here, and deliberately not model-generated: it must work
 * with no network, no API key and no backend round trip, because it is the
 * fallback. It carries the two facts that make a first message worth reading:
 * the vacancy they published, and the week it appeared.
 *
 * Deliberately short, deliberately one question. It names the role because a
 * message that could have been sent to anyone reads as one that was.
 */
export function buildOutreachDraft(employer: Employer, contact: ContactRow): { subject: string; body: string } {
    const vacancy = employer.open[0] ?? employer.past[0] ?? null;
    const role = (vacancy?.title ?? "").trim();
    const place = [vacancy?.location_city, vacancy?.location_country].filter(Boolean).join(", ");
    const greeting = firstNameOf(contact) || "buenos días";

    const subject = role
        ? `Candidatos de sostenibilidad para "${role}"`
        : `Candidatos de sostenibilidad para ${employer.name}`;

    const opening = role
        ? `Vi que en ${employer.name} tenéis abierta la vacante "${role}"${place ? ` en ${place}` : ""}.`
        : `Os escribo porque en ${employer.name} estáis contratando perfiles de sostenibilidad.`;

    const body = [
        `Hola ${greeting},`,
        "",
        opening,
        "",
        "En SustenTalent trabajamos con profesionales de sostenibilidad y ESG en España y Latinoamérica. " +
        "Tenemos candidatos que encajan con ese perfil y que están disponibles ahora mismo.",
        "",
        "¿Te viene bien que te envíe dos o tres perfiles esta semana?",
        "",
        "Un saludo,"
    ].join("\n");

    return { subject, body };
}

export function mailtoUrl(email: string, draft: { subject: string; body: string }): string {
    return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
}

// ── Reading a whole collection ───────────────────────────────────────

/** Page size for the prospect-side collections, which are read whole. */
export const PAGE_SIZE = 500;

/**
 * Every row of a collection, paged to exhaustion rather than capped.
 *
 * These tables are state, not evidence: a missing prospect shows an employer as
 * "sin contactar" when they were emailed last week, and a missing contact gets
 * a person added twice. There is at most one row per employer anyone has
 * touched, and a handful of contacts each, so reading them all stays cheap for
 * far longer than this business will need.
 */
export async function fetchAllRows<T>(data: any, slug: string): Promise<T[]> {
    const all: T[] = [];
    for (let offset = 0; ; offset += PAGE_SIZE) {
        const res = await data.collection(slug).find({ limit: PAGE_SIZE, offset });
        const rows: T[] = res?.data ?? [];
        all.push(...rows);
        // Stop on a short page, and on a full one the total says is the last —
        // a server ignoring `offset` would otherwise loop forever.
        if (rows.length < PAGE_SIZE) break;
        if (all.length >= (res?.meta?.total ?? all.length)) break;
    }
    return all;
}

// ── The market ───────────────────────────────────────────────────────

/**
 * Where this business sells: Spain and Latin America.
 *
 * The scrape reads job boards that also carry the United States, Canada,
 * Germany and the UK, and on 2026-09-06 the first employer in "Por hacer" was
 * a consultancy in Massachusetts. An employer outside the market is still
 * evidence — they hire for sustainability — and still not a call to make
 * this morning. Country names as the offers spell them, in Spanish, accents
 * folded; a country nobody spelled is unknown rather than foreign, so a
 * remote posting or one with no location is never hidden by this.
 */
const MARKET_COUNTRIES = new Set([
    "espana", "spain", "portugal",
    "mexico", "guatemala", "honduras", "el salvador", "nicaragua", "costa rica", "panama",
    "colombia", "venezuela", "ecuador", "peru", "bolivia", "chile", "argentina", "uruguay",
    "paraguay", "brasil", "brazil", "republica dominicana", "dominican republic", "cuba", "puerto rico",
    "latinoamerica", "latam", "america latina", "hispanoamerica"
]);

function foldCountry(name: string): string {
    return name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

export type MarketFit = "in" | "unknown" | "out";

/**
 * In the market, unknown, or out of it. "In" wins when any of an employer's
 * postings is: a company hiring in Bogotá and Houston is a company hiring in
 * Bogotá.
 */
export function marketFit(countries: readonly string[]): MarketFit {
    const known = countries.map(foldCountry).filter(Boolean);
    if (known.length === 0) return "unknown";
    if (known.some(c => MARKET_COUNTRIES.has(c))) return "in";
    return "out";
}

/** Sort key: in-market first, then unknown, then out. */
export const MARKET_ORDER: Record<MarketFit, number> = { in: 0, unknown: 1, out: 2 };

// ── Whether an offer still says "hiring now" ─────────────────────────

/**
 * Six weeks. Job postings go stale on their own in about a month; past six
 * an offer says the employer hires for these roles, not that they are
 * hiring today. Deliberately independent of the review queue's own clock
 * (`STALE_SCRAPE_DAYS`, 14 days): whether the team got round to approving a
 * posting for the board is no evidence about the employer.
 */
export const OFFER_LIVE_DAYS = 45;

export function isRecentOffer(offer: Pick<ScrapedRow, "scraped_at" | "posted_at" | "status">, now: Date = new Date()): boolean {
    if (offer.status === "rejected") return false;
    const iso = offer.posted_at ?? offer.scraped_at ?? null;
    if (!iso) return false;
    const when = Date.parse(iso);
    if (!Number.isFinite(when)) return false;
    return now.getTime() - when <= OFFER_LIVE_DAYS * 86_400_000;
}
